import cn from 'clsx'
import React, { useMemo, useState, useCallback, useContext } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import Heading from 'components/Luxkit/Typography/Heading'
import BodyText from 'components/Luxkit/Typography/BodyText'
import LineAngleLeftBIcon from 'components/Luxkit/Icons/line/LineAngleLeftBIcon'
import LineAngleRightBIcon from 'components/Luxkit/Icons/line/LineAngleRightBIcon'
import TextButton from 'components/Luxkit/Button/TextButton'
import moment from 'moment'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import { MONTH_NAMES } from 'constants/dates'
import { without } from 'lib/arrayUtils'
import Clickable from 'components/Common/Clickable/Clickable'

const MonthGridWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${rem(8)};
`

const YearsControl = styled.div`
  margin-top: ${rem(16)};
  display: flex;
  align-items: center;
  gap: ${rem(16)};
`

const MonthGrid = styled.div`
  margin-top: ${rem(16)};
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${rem(16)};
  grid-auto-rows: ${rem(60)};

  ${mediaQueryUp.xs} {
    grid-template-columns: 1fr 1fr 1fr;
  }

  ${mediaQueryUp.sm} {
    grid-template-columns: 1fr 1fr 1fr 1fr;
    grid-auto-rows: ${rem(64)};
  }
`

const MonthGridItem = styled(Clickable)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${rem(12)};
  border: 1px solid ${({ theme }) => theme.palette.neutral.five};
  border-radius: ${props => props.theme.borderRadius.medium};
  transition: border-color 0.2s, box-shadow 0.2s, color 0.2s;
  gap: ${rem(4)};

  &:not(:disabled) {
    &:hover {
      border-color: ${props => props.theme.palette.neutral.one};
    }

    .input-keyboard &:focus {
      outline: 2px solid ${props => props.theme.palette.neutral.five};
      outline-offset: 2px;
    }

    &.selected {
      box-shadow: inset 0px 0px 0px 2px ${({ theme }) => theme.palette.neutral.one};
    }
  }


  &:disabled {
    color: ${props => props.theme.palette.neutral.four};
  }
`

// const SkeletonPrice = styled(LoadingBox)`
//   min-height: ${rem(12)};
//   border-radius: ${props => props.theme.borderRadius.medium};
//   width: ${rem(130)};
// `

interface Props {
  onSelect?: () => void;
  onChange?: (value: App.CruiseGlobalFilters) => void;
}

const currentYear = new Date().getFullYear()
const startOfMonth = moment().startOf('month')
function CruiseWhenMonth({
  onSelect,
  onChange,
}: Props) {
  const {
    // cruiseLines: searchCruiseLines,
    // searchItem,
    // secondarySearchItem,
    flexibleMonths,
    // flexibleNights,
  } = useContext(GlobalSearchStateContext)
  const searchDispatch = useContext(GlobalSearchDispatchContext)

  // const queryParams = useQueryParams()
  // const destinationId = searchItem?.value
  // const departureId = secondarySearchItem?.value
  // const rateCodes = useMemo(() => queryParams.getAll('rateCodes'), [queryParams])
  const [selectedYear, setYear] = useState(currentYear)

  const departureMonths = useMemo(() => flexibleMonths?.split(',') ?? [], [flexibleMonths])
  // const lowestPriceParams = useMemo(() => {
  //   const year = selectedYear
  //   const baseDate = new Date()

  //   if (year !== baseDate.getFullYear()) {
  //     baseDate.setDate(1)
  //     baseDate.setMonth(0)
  //     baseDate.setFullYear(year)
  //   }

  //   const from = moment().set({ year: baseDate.getFullYear(), month: baseDate.getMonth(), date: baseDate.getDate() }).format('YYYY-MM-DD')
  //   const until = moment().set({ year, month: 11, date: 31 }).endOf('year').format('YYYY-MM-DD')
  //   const [durationMin, durationMax] = getFlexibleNightRange(flexibleNights)
  //   return {
  //     from,
  //     until,
  //     destinationId,
  //     departureId,
  //     durationMin,
  //     durationMax,
  //     rateCodes,
  //     cruiseLines: searchCruiseLines,
  //   }
  // }, [departureId, destinationId, flexibleNights, rateCodes, searchCruiseLines, selectedYear])

  // const [lowestPrices, fetchingPrices] = useCruiseLowestPricesByMonth(lowestPriceParams)

  // const monthData = useMemo(() => {
  //   return arrayToObject(lowestPrices, price => `${price.year}-${price.month}`)
  // }, [lowestPrices])

  // when the previuos filters changes, check if the selected month is still valid, if not, remove it
  // useEffect(() => {
  //   if (!fetchingPrices) {
  //     const nextMonths = departureMonths.filter((month) => !!monthData[month])
  //     if (nextMonths.length < departureMonths.length) {
  //       searchDispatch({
  //         type: GlobalSearchStateActions.SET_FLEXIBLE_MONTH_RANGE,
  //         flexibleMonths: nextMonths.join(','),
  //       })
  //     }
  //   }
  // // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [lowestPrices, fetchingPrices])

  // actions
  const nextYear = useCallback(() => {
    setYear(selectedYear + 1)
  }, [selectedYear])

  const prevYear = useCallback(() => {
    setYear(selectedYear - 1)
  }, [selectedYear])

  const onMonthSelect = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const month = e.currentTarget.dataset.month
    let nextMonths = []
    const alreadyExists = departureMonths.includes(month)

    if (alreadyExists) {
      nextMonths = without(departureMonths, month)
    } else {
      nextMonths = [...departureMonths, month]
    }
    if (onChange) onChange({ flexibleMonths: nextMonths.join(',') })
    searchDispatch({
      type: GlobalSearchStateActions.SET_FLEXIBLE_MONTH_RANGE,
      flexibleMonths: nextMonths.join(','),
    })

    if (onSelect) onSelect()
  }, [departureMonths, searchDispatch, onSelect, onChange])

  return <MonthGridWrapper>
    <Heading variant="heading6">When would you like to sail?</Heading>

    <YearsControl>
      <TextButton
        kind="tertiary"
        onClick={prevYear}
        startIcon={<LineAngleLeftBIcon />}
        disabled={selectedYear === currentYear}
      >
      </TextButton>

      <Heading variant="heading6">{selectedYear}</Heading>

      <TextButton
        kind="tertiary"
        onClick={nextYear}
        disabled={selectedYear === (currentYear + 3)}
        startIcon={<LineAngleRightBIcon />}
      >
      </TextButton>
    </YearsControl>

    <MonthGrid>
      {MONTH_NAMES.map((monthName, index) => {
        const key = `${selectedYear}-${(index + 1).toString().padStart(2, '0')}`
        // const price = monthData[key]
        const disable = moment(`${key}-01`).isBefore(startOfMonth, 'month')

        return <MonthGridItem
          key={monthName}
          disabled={disable}
          data-month={key}
          className={cn({ selected: departureMonths.includes(key) })}
          onClick={onMonthSelect}
        >
          <BodyText variant="medium" weight="bold">{monthName}</BodyText>
          {/* {fetchingPrices && <SkeletonPrice />}
          {!fetchingPrices && price && <Caption variant="medium">
            <span>From </span>
            <b><FormatCurrency value={price.totalFare} /></b>
            <span> /person</span>
          </Caption>} */}
        </MonthGridItem>
      })}
    </MonthGrid>
  </MonthGridWrapper>
}
export default CruiseWhenMonth
